import {connect, useSelector} from "react-redux";
import {RouteComponentProps, useParams} from "react-router";
import styled from "@emotion/styled";
import {css} from "@linaria/core";
import {Breadcrumbs} from "@rw/breadcrumbs";

import {appLink} from "../../../../../../packages/utils/cars_routes";
import {IStore} from "../../../app/reducers/hybrid_reducer";
import {Voivodeship, voivodeshipNames} from "../../../app/types/locationTypes";
import {CarMakeTypes} from "../../../cars_data/cars_data";
import {Container} from "../../../homepage/components/CarBrandsSection";
import {ICarListOfferResponse} from "../../../meta_data/reducers/meta_data_reducer";
import {getOfferListUrl} from "../url_utils/get_offer_list_url";
import {parseOfferListPath} from "../url_utils/parse_offer_list_path";
import {capitalizeFirstLetter} from "../utils/capitalize_first_letter";
import {formatMakeDisplayName} from "../utils/format_make_display_name";
import {formatModelDisplayName} from "../utils/format_model_display_name";
import {buildCarListH1, buildCityListingIntro} from "../utils/list_location_copy";
import {IBreadcrumb} from "./breadcrumb_types";
import {CarList} from "./CarList";
import {CarListFilters} from "./CarListFilters";
import {SeoLocationLinks} from "./SeoLocationLinks";

export {capitalizeFirstLetter} from "../utils/capitalize_first_letter";
export type {IBreadcrumb} from "./breadcrumb_types";

interface IStateProps {
    carsData: {
        //  meta: IOfferListApiResponseMeta;
        cars: ICarListOfferResponse[];

        //requestState: RequestState;
    };
    carMakeType: CarMakeTypes;
    totalPages: number;
    totalItems: number;
    currentPage: number;
}

export interface ICarsListDynamicProps extends IStateProps, RouteComponentProps<{}> {
    test?: boolean;
}

export const generateCarListBreadcrumbs = (carMakeType: CarMakeTypes, model?: string, location?: string, subLocation?: string, cityLabel?: string) => {
    const listingBreadcrumbs: IBreadcrumb[] = [
        {
            label: "Osobowe",
            link: appLink.searchResult.universal({offerType: "osobowe"}),
            asStatic: false
        }
    ];

    if (carMakeType) {
        const makeSlug = String(carMakeType).toLowerCase();
        listingBreadcrumbs.push({
            label: formatMakeDisplayName(carMakeType),
            link: getOfferListUrl({
                offerType: "osobowe",
                type: makeSlug
            })
        });

        if (model) {
            listingBreadcrumbs.push({
                label: formatModelDisplayName(model),
                link: getOfferListUrl({
                    offerType: "osobowe",
                    type: makeSlug,
                    model
                })
            });
        }

        if (location && subLocation) {
            listingBreadcrumbs.push({
                label: cityLabel || capitalizeFirstLetter(subLocation),
                link: getOfferListUrl({
                    offerType: "osobowe",
                    type: makeSlug,
                    model: model || undefined,
                    location,
                    subLocation
                })
            });
        } else if (location) {
            listingBreadcrumbs.push({
                label: capitalizeFirstLetter(location),
                link: getOfferListUrl({
                    offerType: "osobowe",
                    type: makeSlug,
                    model: model || undefined,
                    location
                })
            });
        }

        return listingBreadcrumbs;
    }

    return listingBreadcrumbs;
};

export const CarListDynamicC = (props: ICarsListDynamicProps) => {
    const location = useSelector((store: IStore) => store.carList.location.location);
    const locationPLName = useSelector((store: IStore) => store.carList.locationPLName);
    const locationRegions = useSelector((store: IStore) => store.carList.carsLocations);
    const latestModel = useSelector((store: IStore) => (store.carList.latestQuery as {model?: string}).model);
    const routeParams = useParams<{
        type?: string;
        location?: string;
        subLocation?: string;
        subSubLocation?: string;
    }>();
    const parsed = parseOfferListPath(routeParams || {});
    const model = parsed.model || latestModel;
    const breadcrumbsData = generateCarListBreadcrumbs(
        props.carMakeType,
        model,
        location?.location || parsed.location,
        location?.subLocation || parsed.subLocation,
        locationPLName?.cityName
    );

    const make = formatMakeDisplayName(props.carMakeType);
    const modelLabel = formatModelDisplayName(model);
    const makeModel = make && modelLabel ? `${make} ${modelLabel}` : make || modelLabel;
    const regionSlug = location?.location || parsed.location;
    const citySlug = location?.subLocation || parsed.subLocation;
    let place = "";
    if (citySlug) {
        place = locationPLName?.cityName || capitalizeFirstLetter(citySlug);
    } else if (regionSlug) {
        const slug = regionSlug as Voivodeship;
        place = voivodeshipNames[slug] || capitalizeFirstLetter(regionSlug);
    }
    const headingInput = {
        makeModel: makeModel || undefined,
        place: place || undefined,
        count: props.totalItems,
        page: props.currentPage
    };
    const regionName = regionSlug ? voivodeshipNames[regionSlug as Voivodeship] : "";
    const intro = buildCityListingIntro({
        count: props.totalItems,
        cityName: citySlug ? place : undefined,
        citySlug,
        voivodeshipName: regionName || undefined,
        makeModel: makeModel || undefined,
        otherCities: (locationRegions?.cities || []).map((city) => ({
            city: city.city,
            citySlug: city.citySlug,
            offerCount: city.offer_count
        }))
    });

    return (
        <div className={listingHolder}>
            <div className={pageHolder}>
                <Container>
                    <CarListBreadcrumbs breadcrumbs={breadcrumbsData} />
                    <h1>{buildCarListH1(headingInput)}</h1>
                    {intro ? <p className={introStyle}>{intro}</p> : null}
                    <CarListFilters />
                    <CarList
                        cars={props.carsData.cars}
                        totalPages={props.totalPages}
                        totalItems={props.totalItems}
                        currentPage={props.currentPage}
                        carMakeType={props.carMakeType}
                    />
                    <SeoLocationLinks />
                </Container>
            </div>
        </div>
    );
};

function mapStateToProps(state: IStore): IStateProps {
    return {
        carsData: state.carList.cars,
        totalPages: state.carList.cars.totalPages,
        totalItems: state.carList.cars.totalItems,
        currentPage: state.carList.cars.currentPage,
        carMakeType: state.carList.latestQuery.type
    };
}

export const CarListDynamic = connect(mapStateToProps, null)(CarListDynamicC);

const listingHolder = css`
    position: relative;

    padding: 0 2rem;

    @media (min-width: 1230px) {
        padding: 0;
    }
`;

const pageHolder = css`
    position: relative;
    width: 100%;
`;

const introStyle = css`
    max-width: 72rem;
    margin: 0 0 1.6rem;
    font-size: 1.6rem;
    line-height: 1.5;
`;
const CarListBreadcrumbs = styled(Breadcrumbs)`
    overflow-x: auto;
    overflow-y: hidden;
    flex-wrap: wrap;
`;
